import { dateFnsLocalizer, Formats, Messages } from 'react-big-calendar'
import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import parse from 'date-fns/parse'
import startOfWeek from 'date-fns/startOfWeek'
import { localization } from '@ocean/configs'
import { FilterUtils, ToastUtils, LoggerUtils, DateUtils } from '@ocean/utils'
import {
  RangePeriod,
  RangeDatePeriod,
  RangeChangeProp,
  EventStatus,
  BaseEvent,
  ReferenceRecord,
} from '../types'

const { i18n } = localization

const I18_PATH = 'planner'

class BigCalendarUtils {
  I18_PATH = I18_PATH

  // In minutes
  MINIMUM_EVENT_DURATION = 15
  DEFAULT_SHUTDOWN_DURATION = 10
  DEFAULT_LAUNCH_DURATION = 10
  CALENDAR_STEP = 5
  CALENDAR_TIMESLOTS = 12

  locales = {
    'en-US': require('date-fns/locale/en-US'),
  }

  localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales: this.locales,
  })

  FORMATS: Formats = {
    dayHeaderFormat: 'MMMM dd, yyyy',
  }

  getMessages(): Messages {
    return {
      date: i18n.t(`${I18_PATH}.date`),
      time: i18n.t(`${I18_PATH}.time`),
      event: i18n.t(`${I18_PATH}.event`),
      allDay: i18n.t(`${I18_PATH}.allDay`),
      week: i18n.t(`${I18_PATH}.week`),
      work_week: i18n.t(`${I18_PATH}.work_week`),
      day: i18n.t(`${I18_PATH}.day`),
      month: i18n.t(`${I18_PATH}.month`),
      previous: i18n.t(`${I18_PATH}.previous`),
      next: i18n.t(`${I18_PATH}.next`),
      yesterday: i18n.t(`${I18_PATH}.yesterday`),
      tomorrow: i18n.t(`${I18_PATH}.tomorrow`),
      today: i18n.t(`${I18_PATH}.today`),
      agenda: i18n.t(`${I18_PATH}.agenda`),
      showMore: count => i18n.t(`${I18_PATH}.showMore`, { count }),
      noEventsInRange: i18n.t(`${I18_PATH}.noEventsInRange`),
    }
  }

  INACTIVE_CLASSNAME = 'inactive'
  CURRENT_BOOKING_CLASSNAME = 'current'
  STATUS_CLASSNAMES = {
    [EventStatus.Ongoing]: 'ongoing',
    [EventStatus.Upcoming]: 'upcoming',
    [EventStatus.Past]: 'past',
  }

  getStatusLocalization(): Record<EventStatus, string> {
    return {
      [EventStatus.Ongoing]: i18n.t(`${I18_PATH}.statuses.ongoingNow`),
      [EventStatus.Upcoming]: i18n.t(`${I18_PATH}.statuses.upcoming`),
      [EventStatus.Past]: i18n.t(`${I18_PATH}.statuses.past`),
    }
  }

  getReferences(): ReferenceRecord[] {
    const statuses = this.getStatusLocalization()
    return [
      {
        title: statuses[EventStatus.Ongoing],
        color: 'danger',
      },
      {
        title: i18n.t(`${I18_PATH}.current`),
        color: 'greenLight',
      },

      {
        title: i18n.t(`${I18_PATH}.preparation`),
        color: 'gallery',
        pattern: true,
      },
      {
        title: statuses[EventStatus.Upcoming],
        color: 'oceanDark',
      },
      {
        title: statuses[EventStatus.Past],
        color: 'haze',
      },
    ]
  }

  getDateMonthPeriod(date: Date): RangePeriod {
    const startDate = FilterUtils.formatDate(DateUtils.startOfMonth(date))
    const endDate = FilterUtils.formatDate(DateUtils.endOfMonth(date))
    return { startDate, endDate }
  }

  getRangePeriod(range: RangeChangeProp): RangePeriod {
    let dateRange: RangeDatePeriod
    if (Array.isArray(range)) {
      if (range.length === 1) {
        const date = range[0]
        return this.getDateMonthPeriod(date)
      } else {
        const startDate = range[0]
        const endDate = DateUtils.endOfDay(range[range.length - 1])
        dateRange = { startDate, endDate }
      }
    } else {
      dateRange = { startDate: new Date(range.start), endDate: new Date(range.end) }
    }

    const startDate = FilterUtils.formatDate(dateRange.startDate)
    const endDate = FilterUtils.formatDate(dateRange.endDate)

    return { startDate, endDate }
  }

  getEventStatus(event: BaseEvent, dateNow?: number) {
    const { start: startDate, end: endDate } = event
    const now = dateNow || Date.now()
    const start = new Date(startDate as Date)
    const end = new Date(endDate as Date)

    if (end < start) return null

    try {
      switch (true) {
        case DateUtils.isBefore(end, now):
          return EventStatus.Past
        case DateUtils.isWithinInterval(now, { start, end }):
          return EventStatus.Ongoing
        case DateUtils.isAfter(start, now):
        default:
          return EventStatus.Upcoming
      }
    } catch (e) {
      LoggerUtils.error('getEventStatus error', e)
      return null
    }
  }

  newEventId(sessions: BaseEvent[]) {
    const idList = sessions.map(a => +a.id || 0)
    if (!idList.length) return '0'
    return `${Math.max(...idList) + 1}`
  }

  checkValidStartDate(
    startDate: Date,
    getNow: (() => Date) | undefined,
    launchDurationGap: number
  ): boolean {
    const dateNow = getNow ? getNow() : new Date()
    const validStartDate = DateUtils.addMinutes(dateNow, launchDurationGap)
    if (startDate < validStartDate) {
      ToastUtils.error(i18n.t(`${I18_PATH}.startDateCannotBeInThePast`))
      return false
    }
    return true
  }
}

export default new BigCalendarUtils()
